import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, MessageCircleCode } from "lucide-react";
import { useAuth } from "@clerk/react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useChatStore, useSidebarGroup } from "@/stores";
import type { ConversationSummary } from "@/stores/chat/chatStore";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { NavMorePopover } from "./nav-more-popover";

// const BetaBadge = () => (
//   <span className="ml-auto text-[11px] font-normal text-warning rotate-6 border border-dashed bg-background border-warning/80 px-1 rounded-xs">
//     Beta
//   </span>
// );

/** Row label — a skeleton while the AI title of a just-created chat is generating. */
const ConversationTitle = ({ conversation }: { conversation: ConversationSummary }) =>
  conversation.titlePending ? (
    <Skeleton className="h-3 w-24 rounded-xs" />
  ) : (
    <span className="truncate">{conversation.title || "Untitled"}</span>
  );

/** One chat row — same markup inline, in the "+N more" popover and the hover card. */
function ConversationRow({
  conversation,
  active,
}: {
  conversation: ConversationSummary;
  active: boolean;
}) {
  return (
    <SidebarMenuSubButton asChild isActive={active} size="sm">
      <Link to={`/dashboard/amy/${conversation.id}`}>
        <ConversationTitle conversation={conversation} />
      </Link>
    </SidebarMenuSubButton>
  );
}

export function AmyNav() {
  const location = useLocation();
  const { orgId, getToken } = useAuth();
  const { conversations, loadConversations } = useChatStore();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  // Open state + inline row count come from the user's sidebar prefs
  // (gear next to "Main Menu"); the chevron toggle is remembered.
  const { open, setOpen, itemsShown } = useSidebarGroup("amy");

  // The recent-chats rail is only fetched by the Amy page, so load it here too —
  // the sidebar shows chats from every route. Keyed by org: a switch re-fetches
  // (AmyChat's reset already drops the previous org's threads when it's mounted).
  const loadedOrg = useRef<string | null>(null);
  useEffect(() => {
    if (!orgId || loadedOrg.current === orgId) return;
    loadedOrg.current = orgId;
    (async () => {
      const token = await getToken();
      if (token) loadConversations(token, orgId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const isActive = location.pathname.includes("/dashboard/amy");

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <HoverCard openDelay={0} closeDelay={0}>
          <HoverCardTrigger asChild>
            <SidebarMenuButton isActive={isActive}>
              <MessageCircleCode />
              <span>Ask Amy</span>
            </SidebarMenuButton>
          </HoverCardTrigger>

          <HoverCardContent
            side="right"
            align="start"
            className="w-56 flex flex-col gap-1 p-2 pt-0.5 rounded-xl max-h-96.5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/30%)_transparent] scrollbar-thin"
            sideOffset={-1}
          >
            <Link
              to="/dashboard/amy"
              className="my-2.5 mx-1 mb-1 text-xs font-medium text-muted-foreground hover:text-foreground group"
            >
              Ask Amy{" "}
              <ChevronRight className="size-3.5 inline group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="border-b border-dashed w-auto" />

            {conversations?.map((conversation) => (
              <ScopeWrapper key={conversation.id} color="primary">
                <ConversationRow
                  conversation={conversation}
                  active={location.pathname.includes(conversation.id)}
                />
              </ScopeWrapper>
            ))}
          </HoverCardContent>
        </HoverCard>
      </SidebarMenuItem>
    );
  }

  // itemsShown 0 = the group never expands: no chevron, no sub-list.
  const canExpand = itemsShown > 0 && (conversations?.length ?? 0) > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Ask Amy" isActive={isActive}>
          <Link to="/dashboard/amy">
            <MessageCircleCode />
            <span>Ask Amy</span>
            {/* <BetaBadge /> */}
          </Link>
        </SidebarMenuButton>

        {canExpand && (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="data-[state=open]:rotate-90 hover:bg-primary/10 cursor-pointer rounded-xs">
                <ChevronRight className="text-primary/60 group-hover:text-primary" />
                <span className="sr-only">Toggle</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>

            <CollapsibleContent className="w-full mt-0.5">
              <SidebarMenuSub className="w-full pr-4">
                {conversations.slice(0, itemsShown).map((conversation) => (
                  <ScopeWrapper key={conversation.id} color="primary">
                    <SidebarMenuSubItem>
                      <ConversationRow
                        conversation={conversation}
                        active={location.pathname.includes(conversation.id)}
                      />
                    </SidebarMenuSubItem>
                  </ScopeWrapper>
                ))}
              </SidebarMenuSub>

              <NavMorePopover count={conversations.length - itemsShown}>
                {conversations.slice(itemsShown).map((conversation) => (
                  <ScopeWrapper key={conversation.id} color="primary">
                    <ConversationRow
                      conversation={conversation}
                      active={location.pathname.includes(conversation.id)}
                    />
                  </ScopeWrapper>
                ))}
              </NavMorePopover>
            </CollapsibleContent>
          </>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}
